// /src/components/admin/ReputationTuningTab.jsx
// Reputation Tuning - Adjust reputation algorithm parameters without code changes

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import { RefreshCw, Save } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { logAdminAction, auditFailureMessage } from '../../utils/adminAuditLog';
import ConfirmationModal from '../core/ConfirmationModal';

const ReputationTuningTab = ({ currentUser }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [infoModal, setInfoModal] = useState({ visible: false, message: '' });
  const showInfo = (message) => setInfoModal({ visible: true, message });

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseFetch.query('admin_reputation_config', {
        select: '*',
        order: 'key.asc'
      });

      if (error) {
        console.error('Error loading reputation configs:', error);
        return;
      }

      setConfigs(data || []);
    } catch (err) {
      console.error('Error loading reputation configs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleEdit = (config) => {
    setEditingKey(config.key);
    setEditValue(config.value.toString());
  };

  const handleSave = async (key) => {
    const numericValue = parseFloat(editValue);

    if (isNaN(numericValue)) {
      showInfo(t('enterValidNumber'));
      return;
    }

    const oldValue = configs.find(c => c.key === key)?.value;

    try {
      const { error } = await supabaseFetch.update('admin_reputation_config', key, {
        value: numericValue,
        updated_at: new Date().toISOString(),
        updated_by: currentUser?.id
      });

      if (error) {
        console.error('Error updating config:', error);
        showInfo(t('failedToUpdateConfig'));
        return;
      }

      // target_id uses admin's own ID — config rows have text keys, not UUIDs
      const audit = await logAdminAction({
        actionType: 'reputation_config_update',
        targetType: 'user',
        targetId: currentUser?.id,
        metadata: { config_key: key, old_value: oldValue, new_value: numericValue }
      });

      setEditingKey(null);
      setEditValue('');
      loadConfigs();
      const auditMessage = auditFailureMessage(t, audit);
      if (auditMessage) showInfo(auditMessage);
    } catch (err) {
      console.error('Error updating config:', err);
      showInfo(t('failedToUpdateConfig'));
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: theme?.text?.primary || '#1A1A1A'
    },
    refreshButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: theme?.primary || '#024B99',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      fontWeight: '500'
    },
    notice: {
      backgroundColor: '#fef3c7',
      border: '1px solid #fbbf24',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '20px',
      fontSize: '13px',
      color: '#92400e',
      lineHeight: '1.5'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px'
    },
    th: {
      textAlign: 'left',
      padding: '12px 8px',
      borderBottom: `2px solid ${theme?.border || '#DDD'}`,
      color: theme?.text?.secondary || '#666',
      fontWeight: '600'
    },
    td: {
      padding: '12px 8px',
      borderBottom: `1px solid ${theme?.border || '#DDD'}`,
      color: theme?.text?.primary || '#1A1A1A'
    },
    keyCell: {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: theme?.primary || '#024B99',
      fontWeight: '500'
    },
    descriptionCell: {
      color: theme?.text?.secondary || '#666'
    },
    valueInput: {
      width: '100px',
      padding: '6px 10px',
      borderRadius: '4px',
      border: `1px solid ${theme?.border || '#DDD'}`,
      fontSize: '13px',
      fontFamily: 'monospace'
    },
    valueDisplay: {
      fontFamily: 'monospace',
      fontSize: '14px',
      fontWeight: '600',
      color: theme?.text?.primary || '#1A1A1A'
    },
    actionButton: {
      padding: '6px 12px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '12px',
      cursor: 'pointer',
      fontWeight: '500',
      marginRight: '6px'
    },
    editButton: {
      backgroundColor: theme?.primary || '#024B99',
      color: 'white'
    },
    saveButton: {
      backgroundColor: '#dcfce7',
      color: '#15803d',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    },
    cancelButton: {
      backgroundColor: '#fee2e2',
      color: '#991b1b'
    },
    loadingBox: {
      textAlign: 'center',
      padding: '40px',
      color: theme?.text?.secondary || '#666'
    },
    updatedInfo: {
      fontSize: '11px',
      color: theme?.text?.secondary || '#666',
      marginTop: '4px'
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>{t('reputationAlgorithmTuning')}</div>
          <div style={{ fontSize: '13px', color: theme?.text?.secondary || '#666', marginTop: '4px' }}>
            {t('adjustParametersDescription')}
          </div>
        </div>
        <button
          onClick={loadConfigs}
          disabled={loading}
          style={{
            ...styles.refreshButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'default' : 'pointer'
          }}
        >
          <RefreshCw size={14} />
          {t('refresh')}
        </button>
      </div>

      <div style={styles.notice}>
        <strong>⚠️ {t('integrationRequired')}:</strong> {t('integrationNotice')}
        <code style={{ margin: '0 4px', padding: '2px 6px', backgroundColor: 'white', borderRadius: '3px' }}>
          refresh_content_scores() / refresh_author_reputation()
        </code>
        {t('seeRoadmapTab')}
      </div>

      {loading ? (
        <div style={styles.loadingBox}>{t('loadingReputationConfigs')}</div>
      ) : configs.length === 0 ? (
        <div style={styles.loadingBox}>{t('noConfigsFound')}</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '25%' }}>{t('parameter')}</th>
              <th style={{ ...styles.th, width: '40%' }}>{t('description')}</th>
              <th style={{ ...styles.th, width: '15%' }}>{t('value')}</th>
              <th style={{ ...styles.th, width: '20%' }}>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {configs.map(config => (
              <tr key={config.key}>
                <td style={{ ...styles.td, ...styles.keyCell }}>
                  {config.key}
                </td>
                <td style={{ ...styles.td, ...styles.descriptionCell }}>
                  {config.description}
                </td>
                <td style={styles.td}>
                  {editingKey === config.key ? (
                    <input
                      type="number"
                      step="0.1"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      style={styles.valueInput}
                      autoFocus
                    />
                  ) : (
                    <span style={styles.valueDisplay}>{config.value}</span>
                  )}
                  {config.updated_at && editingKey !== config.key && (
                    <div style={styles.updatedInfo}>
                      {t('updated')}: {new Date(config.updated_at).toLocaleDateString(formattingLocale)}
                    </div>
                  )}
                </td>
                <td style={styles.td}>
                  {editingKey === config.key ? (
                    <>
                      <button
                        onClick={() => handleSave(config.key)}
                        style={{ ...styles.actionButton, ...styles.saveButton }}
                      >
                        <Save size={12} />
                        {t('save')}
                      </button>
                      <button
                        onClick={handleCancel}
                        style={{ ...styles.actionButton, ...styles.cancelButton }}
                      >
                        {t('cancel')}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEdit(config)}
                      style={{ ...styles.actionButton, ...styles.editButton }}
                    >
                      {t('edit')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ConfirmationModal
        visible={infoModal.visible}
        message={infoModal.message}
        onOk={() => setInfoModal({ visible: false, message: '' })}
        okLabel="OK"
      />
    </div>
  );
};

export default ReputationTuningTab;
